import React, { useEffect } from 'react';
import Dotdotdot from 'react-dotdotdot';
import {
  useTemplateTileQuery,
  useCopyTemplateToOrgMutation,
  CopyTemplateToOrgMutation,
} from '@trello/graphql/generated';
import { Button } from '@trello/nachos';
import { MemberAvatarUnconnected } from 'app/src/components/MemberAvatar';
import styles from './TemplateTile.less';
import { forTemplate } from 'app/common/lib/util/i18n';
import {
  smallestPreviewBiggerThan,
  Preview,
} from 'app/common/lib/util/preview';
import classNames from 'classnames';
import { UnsplashTracker } from '@trello/unsplash';
import { Analytics } from '@trello/atlassian-analytics';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { TrelloBlue500 } from 'packages/colors/colors';
import preventDefault from 'app/gamma/src/util/prevent-default';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary/ErrorBoundary';
import { logError } from 'app/scripts/debug/log-error';
import { Feature } from 'app/scripts/debug/constants';

type Board = NonNullable<CopyTemplateToOrgMutation['copyBoardToOrg']>;
const format = forTemplate('templates_tile');

interface TemplateTileProps {
  boardId: string;
  organizationId: string;
  onTemplateCopied: (board: Board) => void;
}

export const getBackgroundStyle = ({
  backgroundUrl,
  backgroundColor,
  preview,
}: {
  backgroundUrl?: string;
  backgroundColor?: string;
  preview?: Preview;
}) => {
  const css: React.CSSProperties = {};
  if (backgroundUrl) {
    css.backgroundImage = `url('${preview?.url || backgroundUrl}')`;
    css.backgroundSize = 'cover';
    css.backgroundPosition = 'center';
  } else {
    css.backgroundColor = backgroundColor || TrelloBlue500;
  }
  return css;
};

export const TemplateTile: React.FC<TemplateTileProps> = ({
  boardId,
  organizationId,
  onTemplateCopied,
}) => {
  const { data, loading } = useTemplateTileQuery({
    variables: {
      boardId,
    },
  });
  const [
    copyTemplate,
    { loading: submitting },
  ] = useCopyTemplateToOrgMutation();

  const title = data?.board?.name;
  const url = data?.board?.url;
  const author = data?.board?.templateGallery?.byline;
  const blurb = data?.board?.templateGallery?.blurb;
  const backgroundColor = data?.board?.prefs?.backgroundColor;
  const backgroundUrl = data?.board?.prefs?.backgroundImage;
  const backgroundImageScaled = data?.board?.prefs?.backgroundImageScaled;
  const avatarUrl = data?.board?.templateGallery?.avatarUrl;
  const avatarShape = data?.board?.templateGallery?.avatarShape;

  const preview = smallestPreviewBiggerThan(backgroundImageScaled, 300, 150);

  const backgroundStyle = getBackgroundStyle({
    backgroundColor: backgroundColor || undefined,
    backgroundUrl: backgroundUrl || undefined,
    preview: preview || undefined,
  });

  useEffect(() => {
    if (backgroundUrl) {
      UnsplashTracker.trackOncePerInterval(backgroundUrl);
    }
  }, [backgroundUrl]);

  if (loading) {
    return null;
  }

  const onClickTemplate = () => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'sectionItem',
      actionSubjectId: 'templateTileSectionItem',
      source: 'teamTemplatePickerSection',
      containers: {
        organization: { id: organizationId },
        board: { id: boardId },
      },
    });
    return true;
  };

  const onClickCopy = async () => {
    try {
      Analytics.sendClickedButtonEvent({
        buttonName: 'copyTemplateButton',
        source: 'teamTemplatePickerSection',
        containers: {
          organization: { id: organizationId },
          board: { id: boardId },
        },
      });

      const { data: mutationData } = await copyTemplate({
        variables: { organizationId, boardId },
      });

      mutationData?.copyBoardToOrg &&
        onTemplateCopied(mutationData?.copyBoardToOrg);

      trackUe({
        category: Noun.TEAM_TEMPLATE_PICKER,
        verb: Verb.COPIES,
        directObj: Noun.TEMPLATE,
        context: {
          boardId,
          boardName: name,
          boardSourceId: mutationData?.copyBoardToOrg?.id || '',
        },
      });

      Analytics.sendCopiedBoardEvent({
        source: 'teamTemplatePickerSection',
        containers: {
          organization: { id: organizationId },
          board: { id: boardId },
        },
        attributes: {
          sourceBoardId: mutationData?.copyBoardToOrg?.id,
          fromTemplate: true,
          isTemplate: false,
          visibility: 'org',
        },
      });
    } catch (err) {
      logError({
        error: err,
        tags: {
          ownershipArea: 'trello-bizteam',
          feature: Feature.TeamTemplatePicker,
        },
      });
    }
  };

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-bizteam',
        feature: Feature.TeamTemplatePicker,
      }}
    >
      <a href={url} onClick={onClickTemplate} className={styles.container}>
        <div className={styles.templateBackground} style={backgroundStyle}>
          <MemberAvatarUnconnected
            className={styles.memberAvatar}
            fullName={author}
            avatars={{
              '30': `${avatarUrl}/30.png`,
              '50': `${avatarUrl}/50.png`,
              '170': `${avatarUrl}/170.png`,
            }}
            size={48}
            avatarClassName={classNames(styles.avatar, {
              [styles.orgAvatar]: avatarShape === 'square',
            })}
          />
        </div>
        <Dotdotdot clamp={1} useNativeClamp>
          <h1 className={styles.title}>{title}</h1>
        </Dotdotdot>
        <Dotdotdot clamp={1} useNativeClamp>
          <p className={styles.secondaryText}>{format('by', { author })}</p>
        </Dotdotdot>
        <Dotdotdot clamp={2} useNativeClamp>
          <p className={styles.secondaryText}>{blurb}</p>
        </Dotdotdot>
        <Button
          isDisabled={submitting}
          className={styles.copyButton}
          appearance="primary"
          shouldFitContainer
          onClick={preventDefault(onClickCopy)}
        >
          Copy template
        </Button>
      </a>
    </ErrorBoundary>
  );
};
